/**
 * Must match CALENDAR_CATEGORIES on the backend (utils/courtEvents/calendarCategory).
 *
 * It lives here beside the DTO that carries it, not with the colours: the colour map is one reader
 * of this type and the API response is where it comes from. A backend test imports the colour map
 * and fails if a category here has no colour, so the two cannot drift apart silently.
 */
export type CalendarCategory =
  | 'reminder'
  | 'deadline'
  | 'virtual'
  | 'trial'
  | 'jp'
  | 'county'
  | 'district'
  | 'probate'
  | 'municipal'
  | 'unknown';

/** Must match APPEARANCE_REQUIREMENTS on the backend — the values are stored verbatim. */
export type AppearanceRequirement = 'NOT_REQUIRED' | 'IN_PERSON' | 'BY_VIDEO' | 'BY_PHONE';

export const APPEARANCE_LABELS: Record<AppearanceRequirement, string> = {
  NOT_REQUIRED: 'Client does not need to appear',
  IN_PERSON: 'Client must appear in person',
  BY_VIDEO: 'Client must appear, by video',
  BY_PHONE: 'Client must appear, by phone',
};

/** Must match APPEARANCE_OUTCOMES on the backend (utils/courtEvents/appearanceOutcome). */
export type AppearanceOutcome = 'UNRECORDED' | 'APPEARED' | 'DID_NOT_APPEAR';

export const APPEARANCE_OUTCOME_LABELS: Record<AppearanceOutcome, string> = {
  UNRECORDED: 'Not recorded yet',
  APPEARED: 'The client appeared',
  DID_NOT_APPEAR: 'The client did NOT appear',
};

/**
 * The outcome question only applies where attendance was actually required — recording "did not
 * appear" against a setting nobody had to attend would manufacture a failure to appear, which on
 * the engagement agreement's terms is a $350–$1,500 fee. Mirrors `outcomeApplies` on the backend,
 * which refuses the write regardless; this just hides the control where it does not belong.
 */
export function appearanceOutcomeApplies(
  requirement: AppearanceRequirement | undefined | null,
): boolean {
  return requirement === 'IN_PERSON' || requirement === 'BY_VIDEO' || requirement === 'BY_PHONE';
}

/** A setting is not "missed" until the whole day it falls on is over. Mirrors `settingHasPassed`. */
export function appearanceSettingHasPassed(
  startEpochMs: number | string,
  now: Date = new Date(),
): boolean {
  // Postgres bigint arrives as a string in JSON. Number.isFinite('1756...') is false, which
  // used to treat every real setting as still in the future.
  const startMs = Number(startEpochMs);
  if (!Number.isFinite(startMs) || startMs <= 0) return false;
  const start = new Date(startMs);
  const endOfDay = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
    23,
    59,
    59,
    999,
  );
  return now.getTime() > endOfDay.getTime();
}

/**
 * Required attendance, the day is over, and nobody has recorded appeared / did not appear.
 * Same three gates as the morning sweep's awaiting list.
 */
export function isAwaitingAppearanceEvent(
  event: {
    CourtEvent_Start: number | string;
    CourtEvent_AppearanceRequirement?: AppearanceRequirement;
    CourtEvent_AppearanceOutcome?: AppearanceOutcome;
  },
  now: Date = new Date(),
): boolean {
  if (!appearanceOutcomeApplies(event.CourtEvent_AppearanceRequirement)) return false;
  if (!appearanceSettingHasPassed(event.CourtEvent_Start, now)) return false;
  const outcome = event.CourtEvent_AppearanceOutcome ?? 'UNRECORDED';
  return outcome === 'UNRECORDED';
}

/** Thrown when the backend refuses the outcome (409) — e.g. recording a failure on a setting nobody had to attend. */
export class AppearanceOutcomeRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AppearanceOutcomeRefusedError';
  }
}

interface CourtEventDto {
  CourtEvent_guid: string;
  CourtEvent_Start: number;
  CourtEvent_End: number;
  CourtEvent_Title: string;
  CourtEvent_Desc: string;
  // Present only on manager-authenticated responses. The client portal never receives these.
  CourtEvent_AppearanceRequirement?: AppearanceRequirement;
  /** Null means nobody has answered the appearance question — the value is just the default. */
  CourtEvent_AppearanceSetBy?: string | null;
  CourtEvent_AppearanceSetAt?: string | null;
  /** Did the client actually turn up? Only meaningful where an appearance was required. */
  CourtEvent_AppearanceOutcome?: AppearanceOutcome;
  CourtEvent_AppearanceOutcomeSetBy?: string | null;
  CourtEvent_AppearanceOutcomeSetAt?: string | null;
  CourtEvent_AppearanceOutcomeNote?: string | null;
  /** False for reminders and deadlines, where the appearance question does not apply. */
  CourtEvent_IsHearing?: boolean;
  /**
   * Which colour this setting takes, worked out on the server. Manager responses only.
   *
   * Optional on purpose: the browser can be a build ahead of the API during a deploy, and a required
   * field against a stale API has already taken a whole page down here once.
   */
  CourtEvent_CalendarCategory?: CalendarCategory;
  Ticket_guid: string;
}

interface TicketDto {
  Ticket_guid: string;
  Ticket_Number: string;
  Ticket_Representation: string;
  Ticket_Address: string;
  Ticket_Citation: string;
  Ticket_City: string;
  Ticket_County: string;
  Ticket_LicenseType: string;
  Ticket_Zone: string;
  Ticket_Url: string;
  Ticket_CourtDate: string;
  Ticket_CreatedAt: string;
  Ticket_Active: boolean;
  Court_id: number | null;
}

interface UserDto {
  User_guid: string;
  User_FirstName: string;
  User_LastName: string;
  User_Active: boolean;
  User_DOB: string | null;
}

interface FullCourtEventDto {
  courtEvent: CourtEventDto;
  ticket: TicketDto;
  user: UserDto;
}

interface CreateCourtEventDto {
  CourtEvent_Start: number;
  CourtEvent_End: number;
  CourtEvent_Title?: string;
  CourtEvent_Desc?: string;
  /**
   * Which of a case's many settings is the trial. The create route has always accepted it and this
   * type never offered it, so no screen could send it — which is part of why the flag is set on none
   * of the upcoming settings and the calendar has to read a trial off the title instead.
   */
  CourtEvent_IsTrialSetting?: boolean;
  Ticket_guid: string; // Use guid instead of id since that's what frontend has
  calendarEventTitle?: string;
  calendarEventLocation?: string;
  calendarEventBody?: string;
}

interface UpdateCourtEventDto {
  CourtEvent_guid: string;
  CourtEvent_Start?: number;
  CourtEvent_End?: number;
  CourtEvent_Title?: string;
  CourtEvent_Desc?: string;
  CourtEvent_AppearanceRequirement?: AppearanceRequirement;
  Ticket_guid?: string;
  calendarEventTitle?: string;
  calendarEventLocation?: string;
  calendarEventBody?: string;
}

class CourtEventApi {
  static async getCourtEvent(CourtEvent_guid: string): Promise<FullCourtEventDto> {
    const queryParams = new URLSearchParams();
    queryParams.append('CourtEvent_guid', CourtEvent_guid);

    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to fetch court event' }));
      throw new Error(errorData.error || 'Failed to fetch court event');
    }

    return await response.json();
  }

  static async listCourtEvents(params: {
    User_guid?: string;
    Ticket_guid?: string;
    Court_guid?: string;
    CourtEvent_Start_From?: number;
    CourtEvent_Start_To?: number;
  }): Promise<CourtEventDto[]> {
    console.log('Listing court events with params:', params);
    const queryParams = new URLSearchParams();
    
    if (params.User_guid) queryParams.append('User_guid', params.User_guid);
    if (params.Ticket_guid) queryParams.append('Ticket_guid', params.Ticket_guid);
    if (params.Court_guid) queryParams.append('Court_guid', params.Court_guid);
    if (params.CourtEvent_Start_From) queryParams.append('CourtEvent_Start_From', params.CourtEvent_Start_From.toString());
    if (params.CourtEvent_Start_To) queryParams.append('CourtEvent_Start_To', params.CourtEvent_Start_To.toString());

    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/list?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to fetch court events' }));
      throw new Error(errorData.error || 'Failed to fetch court events');
    }

    return await response.json();
  }

  static async createCourtEvent(courtEvent: CreateCourtEventDto): Promise<CourtEventDto> {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(courtEvent),
      }
    );

    if (!response.ok) {
      /*
       * The route answers a 400 with `{ message, errors }` and other failures with `{ error }`.
       * Reading only `error` turned every validation failure into "Failed to create court event",
       * which tells the person nothing — and the checks it can fail are the ones a form cannot
       * catch, such as the millisecond floor on the timestamps.
       */
      const errorData = await response.json().catch(() => ({}));
      const detail =
        Array.isArray(errorData.errors) && errorData.errors[0]?.msg ? `: ${errorData.errors[0].msg}` : '';
      throw new Error(
        errorData.error || (errorData.message ? `${errorData.message}${detail}` : 'Failed to create court event'),
      );
    }

    return await response.json();
  }

  /**
   * The PATCH response carries this when the appearance answer changed, so the screen can report
   * what actually reached the client instead of assuming a send happened.
   */
  static async updateCourtEvent(
    courtEvent: UpdateCourtEventDto,
  ): Promise<
    CourtEventDto & {
      appearanceNotification?: { sent: boolean; skippedReason: string | null } | null;
    }
  > {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(courtEvent),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to update court event' }));
      throw new Error(errorData.error || 'Failed to update court event');
    }

    return await response.json();
  }

  /**
   * Record whether the client actually appeared at a past setting (Lloyd, Aug 21 2026). Separate
   * endpoint from the general PATCH because it carries a guard the others do not — see
   * AppearanceOutcomeRefusedError.
   */
  static async recordAppearanceOutcome(input: {
    CourtEvent_guid: string;
    CourtEvent_AppearanceOutcome: AppearanceOutcome;
    CourtEvent_AppearanceOutcomeNote?: string | null;
  }): Promise<CourtEventDto> {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/appearance-outcome`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      },
    );

    if (response.status === 409) {
      const err = await response.json().catch(() => ({}));
      throw new AppearanceOutcomeRefusedError(
        err.error || 'That outcome cannot be recorded for this setting.',
      );
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to record the outcome' }));
      throw new Error(err.error || 'Failed to record the outcome');
    }
    return response.json();
  }

  static async deleteCourtEvent(CourtEvent_guid: string): Promise<{ message: string; courtEvent: CourtEventDto }> {
    const queryParams = new URLSearchParams();
    queryParams.append('CourtEvent_guid', CourtEvent_guid);

    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/?${queryParams.toString()}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to delete court event' }));
      throw new Error(errorData.error || 'Failed to delete court event');
    }

    return await response.json();
  }
}

export default CourtEventApi;
export type { CourtEventDto, FullCourtEventDto, CreateCourtEventDto, UpdateCourtEventDto, TicketDto, UserDto };
